import React, { useEffect, useRef, useState } from 'react';
import { X, Sparkles, Loader2, Play, CheckCircle, AlertTriangle, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

const EXAMPLE = `BOSCH ; F 026 407 125 ; filtre à huile moteur
BREMBO ; P 68 033 ; plaquettes de frein avant Clio 4
MANN-FILTER ; C 30 005 ; filtre à air ; https://exemple.fr/page-de-reference`;

const STATUS = { 'en attente': ['text-gray-500', Clock], 'en cours': ['text-violet-700', Loader2], 'créé': ['text-green-700', CheckCircle], 'erreur': ['text-red-600', AlertTriangle] };

// "Création en série IA": paste lines → background job creating draft (unpublished) product sheets
const ProductAiBatchModal = ({ onClose, onCreated }) => {
  const [lines, setLines] = useState('');
  const [stock, setStock] = useState(0);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const refresh = async () => {
    try { const jobs = await adminAPI.assistBatches(); if (jobs[0]) setJob(jobs[0]); } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); return () => clearInterval(timer.current); }, []);
  useEffect(() => {
    clearInterval(timer.current);
    if (job?.status === 'en cours') timer.current = setInterval(refresh, 3000);
    else if (job?.status === 'terminé' && job.created > 0) onCreated?.();
    return () => clearInterval(timer.current);
  }, [job?.status, job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (lines.trim().length < 3) { toast.error('Collez au moins une ligne « MARQUE ; RÉFÉRENCE »'); return; }
    setStarting(true);
    try { const j = await adminAPI.assistBatch({ lines, publish: false, default_stock: Number(stock) || 0 }); setJob(j); toast.success(`${j.total} fiche(s) en cours de rédaction`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Impossible de lancer la série'); }
    finally { setStarting(false); }
  };

  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ai-batch-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Sparkles className="text-violet-600" size={20} /> Création en série IA</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" data-testid="ai-batch-close"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <p className="text-sm text-gray-600">Une ligne par pièce : <code className="bg-gray-100 px-1 rounded">MARQUE ; RÉFÉRENCE ; indication (optionnel) ; URL de référence (optionnel)</code>. Les fiches sont créées <strong>non publiées</strong>, prix à 0 € (ou fourchette IA), à relire avant mise en ligne.</p>
          <textarea data-testid="ai-batch-lines" value={lines} onChange={(e) => setLines(e.target.value)} rows={7} placeholder={EXAMPLE} className="w-full rounded-lg border border-gray-300 p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-400" disabled={job?.status === 'en cours'} />
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-gray-700 flex items-center gap-2">Stock initial <input type="number" min={0} value={stock} onChange={(e) => setStock(e.target.value)} className="w-20 h-8 rounded border border-gray-300 px-2 text-sm" data-testid="ai-batch-stock" /></label>
            <Button data-testid="ai-batch-start" onClick={start} disabled={starting || job?.status === 'en cours'} className="bg-violet-600 hover:bg-violet-700 text-white ml-auto">
              {starting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />} Lancer la rédaction
            </Button>
          </div>
          {job && (
            <div className="rounded-lg border bg-gray-50 p-4" data-testid="ai-batch-job">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-medium text-gray-800">{job.status === 'en cours' ? 'Rédaction en cours…' : 'Série terminée'} — {job.done}/{job.total} · <span className="text-green-700">{job.created} créée(s)</span>{job.failed > 0 && <> · <span className="text-red-600">{job.failed} erreur(s)</span></>}</span>
                <span className="text-xs text-gray-500">{new Date(job.created_at).toLocaleString('fr-FR')}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 overflow-hidden mb-3"><div className="h-full bg-violet-600 transition-all" style={{ width: `${pct}%` }} data-testid="ai-batch-progress" /></div>
              <ul className="divide-y text-sm max-h-64 overflow-y-auto" data-testid="ai-batch-items">
                {job.items.map((it, i) => { const [cls, Icon] = STATUS[it.status] || STATUS['en attente']; return (
                  <li key={i} className="py-1.5 flex items-center gap-2" data-testid={`ai-batch-item-${i}`}>
                    <Icon size={14} className={`${cls} shrink-0 ${it.status === 'en cours' ? 'animate-spin' : ''}`} />
                    <span className="font-mono text-xs text-gray-700 w-40 truncate">{it.brand} {it.ref}</span>
                    <span className="flex-1 truncate text-gray-800">{it.name || it.hint || ''}{it.error && <span className="text-red-600"> — {it.error}</span>}</span>
                    {it.status === 'créé' && <><span className="text-[11px] text-gray-500">{it.compat} compat. · confiance {it.confidence}</span><a href={`/admin?tab=products&q=${encodeURIComponent(it.ref)}`} className="text-violet-700" title="Voir dans la liste"><ExternalLink size={13} /></a></>}
                    <span className={`text-xs ${cls}`}>{it.status}</span>
                  </li>
                ); })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductAiBatchModal;
